import { useMemo, useState } from "react";
import { useApps } from "../hook";
import { App } from "../types/app";
import { Setting } from "../types/setting";
import { useAddSettings, useSetSetting, useSettingValue } from "./setting";
import { useUserEmail } from "./user";

// The per-user Setting that holds the caller's favorite workspace ids as a JSON array.
export const FAVORITE_APP_IDS_KEY = "favorite-app-ids";
export const FAVORITES_SAVE_ERROR = "Couldn't save favorites. Try again.";

// Missing, empty, malformed JSON, or a non-array value means no favorites. An array keeps its string
// entries in order, drops every other entry, and removes duplicates. Never throws.
export const parseFavoriteAppIds = (raw: string | undefined): string[] => {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const ids: string[] = [];
  for (const entry of parsed) {
    if (typeof entry === "string" && !ids.includes(entry)) ids.push(entry);
  }
  return ids;
};

export const serializeFavoriteAppIds = (ids: string[]): string => JSON.stringify(ids);

// The stored ids that can be rendered: stale ids (workspaces the caller no longer belongs to) are
// ignored for display and order, and are dropped from the next write.
export const presentFavoriteIds = (
  favoriteAppIds: string[],
  apps: App[]
): string[] => {
  const present = new Set(apps.map((app) => app.id));
  return favoriteAppIds.filter((id) => present.has(id));
};

export const useWorkspaceFavorites = () => {
  const [apps] = useApps();
  const settingValue = useSettingValue(FAVORITE_APP_IDS_KEY);
  const setSetting = useSetSetting();
  const addSettings = useAddSettings();
  const user = useUserEmail();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const favorites = useMemo(
    () => presentFavoriteIds(parseFavoriteAppIds(settingValue), apps),
    [settingValue, apps]
  );

  const isFavorite = (appId: string) => favorites.includes(appId);
  const clearError = () => setError(null);

  // Optimistic: useSetSetting merges the new value into state.settings.settingsMap before the write,
  // so the star flips at once. A failed write restores the previous value in Redux only (no second
  // server write) and shows the inline error.
  const toggleFavorite = async (appId: string) => {
    if (!user || isSaving) return;
    setError(null);
    const previousValue = settingValue ?? "";
    const next = favorites.includes(appId)
      ? favorites.filter((id) => id !== appId)
      : [...favorites, appId];
    setIsSaving(true);
    try {
      await setSetting({
        key: FAVORITE_APP_IDS_KEY,
        value: serializeFavoriteAppIds(next),
      });
    } catch {
      const reverted: Setting = {
        user,
        key: FAVORITE_APP_IDS_KEY,
        value: previousValue,
        type: "omega/query-omega/setting/Setting",
      };
      addSettings([reverted]);
      setError(FAVORITES_SAVE_ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  return { favorites, isFavorite, toggleFavorite, isSaving, error, clearError };
};
